import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuthHeaders, fetchBranches } from '../utils/authUtils';
import { API_BASE } from '../config';
import TreeLoader from './common/TreeLoader';

const ROLE_OPTIONS = [
  { value: 'LEVEL1', label: 'Уровень 1 — пользователь' },
  { value: 'LEVEL2', label: 'Уровень 2 — филиал' },
  { value: 'LEVEL3', label: 'Уровень 3 — мл. администратор' },
];

// «Карточки ВНВО» заведены заранее: само право уже выдаётся и хранится,
// а функционал карточек ВНВО появится позже.
export const PERMISSION_OPTIONS = [
  { value: 'MANAGE_OWN_CARDS', label: 'Карточки ВНН' },
  { value: 'MANAGE_OWN_VNVO_CARDS', label: 'Карточки ВНВО' },
  { value: 'CREATE_REPORTS', label: 'Отчёты' },
  { value: 'MANAGE_PLANS', label: 'Планы' },
];

// Право «Планы» доступно только пользователям уровня 2 и 3. Уровню 1 — права на
// карточки (ВНН и ВНВО) и «Отчёты»: ему открыты только отчёты о выполнении.
const LEVEL1_PERMISSIONS = ['MANAGE_OWN_CARDS', 'MANAGE_OWN_VNVO_CARDS', 'CREATE_REPORTS'];

export const isPermissionAllowedForRole = (perm, role) =>
  role !== 'LEVEL1' || LEVEL1_PERMISSIONS.includes(perm);

// Оставляет в списке прав только допустимые для выбранной роли.
const prunePermissionsForRole = (permissions, role) =>
  (permissions || []).filter((p) => isPermissionAllowedForRole(p, role));

// Когда заведена учётная запись (date_joined) — в часовом поясе браузера, без секунд.
export const formatDateJoined = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const btnStyle = (variant = 'primary', disabled = false) => ({
  height: '38px',
  padding: '0 16px',
  borderRadius: '4px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer',
  border: '1px solid #6c757d',
  backgroundColor: variant === 'primary' ? '#6c757d' : '#f8f9fa',
  color: variant === 'primary' ? '#fff' : '#495057',
  opacity: disabled ? 0.55 : 1,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
});

const th = {
  fontSize: '0.82rem',
  fontWeight: 600,
  textAlign: 'left',
  padding: '10px 8px',
  borderBottom: '1px solid #ddd',
  borderRight: '1px solid #ddd',
  backgroundColor: '#f5f5f5',
  color: '#000',
  whiteSpace: 'nowrap',
};

const td = {
  fontSize: '0.85rem',
  padding: '10px 12px',
  borderBottom: '1px solid #e6e6e6',
  borderRight: '1px solid #e6e6e6',
  verticalAlign: 'middle',
  color: '#000',
};

// Ячейка под поле ввода — без внутренних отступов, поле заполняет её целиком
const tdField = {
  ...td,
  padding: 0,
};

// Поле, занимающее всю ячейку (в стиле форм проекта — без видимой рамки)
const cellFieldStyle = {
  width: '100%',
  height: '100%',
  minHeight: '46px',
  boxSizing: 'border-box',
  padding: '8px 12px',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '0.88rem',
  color: '#000',
};

// Кнопка «Удалить» — нейтральная обводка в стиле системы
const dangerBtnStyle = {
  height: '34px',
  padding: '0 16px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 500,
  cursor: 'pointer',
  border: '1px solid #d6a4a4',
  backgroundColor: '#fff',
  color: '#b04a4a',
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
};

// Таблетка права — активная (серый фон) / неактивная (светлая обводка)
const permPillStyle = (active, disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '5px 12px',
  borderRadius: '999px',
  fontSize: '0.8rem',
  fontWeight: 500,
  lineHeight: 1.2,
  cursor: disabled ? 'default' : 'pointer',
  border: active ? '1px solid #6c757d' : '1px solid #ced4da',
  backgroundColor: active ? '#6c757d' : '#f8f9fa',
  color: active ? '#fff' : '#868e96',
  transition: 'all 0.15s ease',
  whiteSpace: 'nowrap',
  opacity: disabled ? 0.6 : 1,
});

// Таблетка права с учётом доступности для выбранной роли.
// Если право недоступно (уровень 1 + Отчёты/Планы) — некликабельна, при наведении
// «горит» красным и показывает подсказку, что недоступно.
const PermissionPill = ({ label, active, available, disabled, onToggle }) => {
  const [hover, setHover] = useState(false);

  if (!available) {
    // Размер таблетки фиксирован: меняется только цвет, текст/иконка остаются прежними,
    // чтобы при наведении ничего не переносилось и таблица не «дрожала».
    return (
      <span
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title="Недоступно для пользователя уровня 1"
        style={{
          ...permPillStyle(false, true),
          cursor: 'not-allowed',
          opacity: 1,
          border: `1px solid ${hover ? '#e57373' : '#ced4da'}`,
          backgroundColor: hover ? '#fdecec' : '#f8f9fa',
          color: hover ? '#c0392b' : '#b9bdc2',
        }}
      >
        <span style={{ fontSize: '0.78rem' }}>✕</span>
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onToggle}
      style={permPillStyle(active, disabled)}
      title={active ? 'Нажмите, чтобы отозвать право' : 'Нажмите, чтобы выдать право'}
    >
      <span style={{ fontSize: '0.78rem' }}>{active ? '✓' : '+'}</span>
      {label}
    </button>
  );
};

// Поле поиска / выбор размера страницы — с рамкой
const searchInputStyle = {
  boxSizing: 'border-box',
  padding: '6px 10px',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '0.85rem',
  height: '38px',
};

const UserAdminRow = React.memo(({
  row,
  edit,
  branches,
  savingId,
  savedId,
  pwdSavedFor,
  onSetValue,
  onSave,
  onOpenPassword,
  onDelete,
}) => {
  const isAdmin = row.role === 'LEVEL4';
  const changed = !!edit && Object.keys(edit).length > 0;
  const getValue = (field) => (
    edit && Object.prototype.hasOwnProperty.call(edit, field) ? edit[field] : row[field]
  );
  const role = getValue('role') || 'LEVEL1';
  const perms = getValue('permissions') || [];
  const branchId = getValue('branch_id');

  const togglePermission = (perm) => {
    const next = perms.includes(perm)
      ? perms.filter((p) => p !== perm)
      : [...perms, perm];
    onSetValue(row.id, 'permissions', next);
  };

  return (
    <tr style={{ backgroundColor: changed ? '#f3f6fb' : (row.id % 2 === 0 ? '#fcfcfd' : '#fff') }}>
      <td style={{ ...td, textAlign: 'center', color: '#6c757d', fontWeight: 500 }}>{row.id}</td>
      <td style={{ ...td, fontWeight: 500 }}>{row.username}</td>
      <td style={tdField}>
        <input
          style={cellFieldStyle}
          value={getValue('full_name') || ''}
          disabled={isAdmin}
          onChange={(ev) => onSetValue(row.id, 'full_name', ev.target.value)}
        />
      </td>
      <td style={tdField}>
        <input
          style={cellFieldStyle}
          value={getValue('phone') || ''}
          disabled={isAdmin}
          onChange={(ev) => onSetValue(row.id, 'phone', ev.target.value)}
        />
      </td>
      <td style={tdField}>
        {isAdmin ? (
          <span style={{ color: '#888', padding: '8px 12px', display: 'inline-block' }}>Администратор</span>
        ) : (
          <select
            style={cellFieldStyle}
            value={role}
            onChange={(ev) => onSetValue(row.id, 'role', ev.target.value)}
          >
            {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
      </td>
      <td style={tdField}>
        <select
          style={cellFieldStyle}
          value={branchId == null ? '' : String(branchId)}
          disabled={isAdmin}
          onChange={(ev) => onSetValue(row.id, 'branch_id', ev.target.value === '' ? null : Number(ev.target.value))}
        >
          <option value="">— не задан —</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </td>
      <td style={{ ...td, textAlign: 'center' }}>
        {isAdmin ? (
          <span style={{ color: '#888', fontSize: '0.8rem' }}>—</span>
        ) : (
          <button type="button" style={btnStyle('secondary')} onClick={() => onOpenPassword(row)}>
            {pwdSavedFor === row.id ? '✓ Изменён' : 'Изменить пароль'}
          </button>
        )}
      </td>
      <td style={td}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {PERMISSION_OPTIONS.map((p) => {
            const active = perms.includes(p.value);
            return (
              <button
                key={p.value}
                type="button"
                disabled={isAdmin}
                onClick={() => togglePermission(p.value)}
                style={permPillStyle(active, isAdmin)}
                title={active ? 'Нажмите, чтобы отозвать право' : 'Нажмите, чтобы выдать право'}
              >
                <span style={{ fontSize: '0.78rem' }}>{active ? '✓' : '+'}</span>
                {p.label}
              </button>
            );
          })}
        </div>
      </td>
      <td style={{ ...td, textAlign: 'center' }}>
        {isAdmin ? (
          <span style={{ color: '#888', fontSize: '0.8rem' }}>—</span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'stretch' }}>
            <button
              type="button"
              style={btnStyle('primary', !changed || savingId === row.id)}
              disabled={!changed || savingId === row.id}
              onClick={() => onSave(row, edit)}
            >
              {savingId === row.id ? 'Сохранение…' : (savedId === row.id ? '✓ Сохранено' : 'Сохранить')}
            </button>
            <button type="button" style={dangerBtnStyle} onClick={() => onDelete(row)}>
              Удалить
            </button>
          </div>
        )}
      </td>
    </tr>
  );
});

// Изолированное окно создания пользователя: держит форму в собственном локальном
// состоянии, поэтому ввод НЕ перерисовывает родителя с тяжёлой таблицей пользователей.
const CreateUserModal = React.memo(({ branches, saving, error, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    username: '', password: '', full_name: '', phone: '',
    role: 'LEVEL1', branch_id: '', permissions: [],
  });

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  // При смене роли убираем недоступные для неё права (уровень 1 — без Отчётов/Планов).
  const setRole = (role) => setForm((prev) => ({
    ...prev,
    role,
    permissions: prunePermissionsForRole(prev.permissions, role),
  }));
  const togglePermission = (perm) => setForm((prev) => ({
    ...prev,
    permissions: prev.permissions.includes(perm)
      ? prev.permissions.filter((p) => p !== perm)
      : [...prev.permissions, perm],
  }));

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '8px', padding: '24px',
        maxWidth: '560px', width: '92%', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px 0', color: '#495057', fontSize: '16px', fontWeight: 600 }}>
          Новый пользователь
        </h3>
        <p style={{ margin: '0 0 18px 0', color: '#6c757d', fontSize: '13px' }}>
          Заполните данные и нажмите «Создать».
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#495057', marginBottom: '6px' }}>Почта (логин)</label>
            <input autoFocus autoComplete="off" style={{ ...searchInputStyle, width: '100%' }}
              value={form.username}
              onChange={(e) => setField('username', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#495057', marginBottom: '6px' }}>Пароль</label>
            <input type="password" autoComplete="new-password" style={{ ...searchInputStyle, width: '100%' }}
              value={form.password}
              onChange={(e) => setField('password', e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', color: '#495057', marginBottom: '6px' }}>ФИО</label>
            <input style={{ ...searchInputStyle, width: '100%' }}
              value={form.full_name}
              onChange={(e) => setField('full_name', e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', color: '#495057', marginBottom: '6px' }}>Телефон</label>
            <input style={{ ...searchInputStyle, width: '100%' }}
              value={form.phone}
              onChange={(e) => setField('phone', e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', color: '#495057', marginBottom: '6px' }}>Уровень</label>
            <select style={{ ...searchInputStyle, width: '100%' }}
              value={form.role}
              onChange={(e) => setRole(e.target.value)}>
              {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', color: '#495057', marginBottom: '6px' }}>Филиал</label>
            <select style={{ ...searchInputStyle, width: '100%' }}
              value={form.branch_id === '' ? '' : String(form.branch_id)}
              onChange={(e) => setField('branch_id', e.target.value)}>
              <option value="">— не задан —</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#495057', marginBottom: '6px' }}>Права</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {PERMISSION_OPTIONS.map((p) => (
                <PermissionPill
                  key={p.value}
                  label={p.label}
                  active={form.permissions.includes(p.value)}
                  available={isPermissionAllowedForRole(p.value, form.role)}
                  disabled={false}
                  onToggle={() => togglePermission(p.value)}
                />
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div style={{ marginTop: '14px', color: '#b04a4a', fontSize: '13px' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button type="button" style={btnStyle('secondary', saving)} disabled={saving}
            onClick={onClose}>Отмена</button>
          <button type="button" style={btnStyle('primary', saving)} disabled={saving}
            onClick={() => onSubmit(form)}>
            {saving ? 'Создание…' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  );
});

// Изолированное окно смены пароля: ввод в поля держится локально и не
// перерисовывает UsersAdminPage с таблицей пользователей на каждый символ.
const ChangePasswordModal = React.memo(({ user, saving, error, onClose, onSubmit }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState(null);

  const submit = () => {
    if (!password) {
      setLocalError('Укажите новый пароль.');
      return;
    }
    if (password !== confirm) {
      setLocalError('Пароли не совпадают.');
      return;
    }
    setLocalError(null);
    onSubmit(password);
  };

  const shownError = localError || error;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '8px', padding: '20px',
        maxWidth: '460px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 6px 0', color: '#495057', fontSize: '16px', fontWeight: 600 }}>
          Смена пароля
        </h3>
        <p style={{ margin: '0 0 16px 0', color: '#6c757d', fontSize: '13px' }}>
          Пользователь: {user.username}
        </p>
        <label style={{ display: 'block', fontSize: '13px', color: '#495057', marginBottom: '6px' }}>Новый пароль</label>
        <input
          type="password"
          autoComplete="new-password"
          autoFocus
          style={{ ...searchInputStyle, width: '100%', marginBottom: '12px' }}
          value={password}
          onChange={(e) => { setPassword(e.target.value); setLocalError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />
        <label style={{ display: 'block', fontSize: '13px', color: '#495057', marginBottom: '6px' }}>Повторите пароль</label>
        <input
          type="password"
          autoComplete="new-password"
          style={{ ...searchInputStyle, width: '100%', marginBottom: shownError ? '8px' : '16px' }}
          value={confirm}
          onChange={(e) => { setConfirm(e.target.value); setLocalError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />
        {shownError && (
          <div style={{ marginBottom: '16px', color: '#b04a4a', fontSize: '13px' }}>{shownError}</div>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" style={btnStyle('secondary', saving)} disabled={saving}
            onClick={onClose}>Отмена</button>
          <button type="button" style={btnStyle('primary', saving)} disabled={saving}
            onClick={submit}>
            {saving ? 'Сохранение…' : 'Изменить пароль'}
          </button>
        </div>
      </div>
    </div>
  );
});

const UsersAdminPage = ({ onBack, embedded = false }) => {
  const [rows, setRows] = useState([]);          // server data
  const [edits, setEdits] = useState({});         // id -> partial edited fields
  const [branches, setBranches] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [numPages, setNumPages] = useState(1);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('');  // '' — все филиалы, 'none' — без филиала
  const searchInputRef = useRef(null);
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, username }
  const [deleting, setDeleting] = useState(false);
  // Поля пароля живут в изолированном ChangePasswordModal — их ввод не
  // перерисовывает родителя с тяжёлой таблицей пользователей.
  const [pendingPassword, setPendingPassword] = useState(null); // { id, username }
  const [pwdError, setPwdError] = useState(null);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdSavedFor, setPwdSavedFor] = useState(null);

  // Создание нового пользователя. Форма живёт в изолированном компоненте
  // CreateUserModal — её ввод не перерисовывает тяжёлую таблицу пользователей.
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (search) params.set('search', search);
      if (branchFilter) params.set('branch', branchFilter);
      const resp = await fetch(`${API_BASE}/auth/users/?${params}`, {
        headers: { ...getAuthHeaders() },
      });
      if (resp.status === 403) {
        setError('Недостаточно прав для просмотра пользователей.');
        setRows([]);
        return;
      }
      if (!resp.ok) {
        setError('Не удалось загрузить список пользователей.');
        setRows([]);
        return;
      }
      const data = await resp.json();
      setRows(Array.isArray(data.results) ? data.results : []);
      setTotal(data.count || 0);
      setNumPages(data.num_pages || 1);
      if (data.page && data.page !== page) {
        setPage(data.page);
      }
      setEdits({});
    } catch (e) {
      setError('Ошибка подключения к серверу.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, branchFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    fetchBranches().then((b) => setBranches(Array.isArray(b) ? b : [])).catch(() => setBranches([]));
  }, []);

  // При появлении ошибки прокручиваем страницу к самому верху, к надписи с ошибкой
  useEffect(() => {
    if (error && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  const setValue = useCallback((id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    setSavedId(null);
  }, []);

  const getValue = useCallback((row, field) => {
    const e = edits[row.id];
    if (e && Object.prototype.hasOwnProperty.call(e, field)) return e[field];
    return row[field];
  }, [edits]);

  const togglePermission = useCallback((row, perm) => {
    const current = getValue(row, 'permissions') || [];
    const next = current.includes(perm)
      ? current.filter((p) => p !== perm)
      : [...current, perm];
    setValue(row.id, 'permissions', next);
  }, [getValue, setValue]);

  const isRowChanged = useCallback((id) => {
    const e = edits[id];
    return e && Object.keys(e).length > 0;
  }, [edits]);

  const saveRow = useCallback(async (row, rowEdits) => {
    const e = rowEdits || edits[row.id];
    if (!e) return;
    setSavingId(row.id);
    setError(null);
    try {
      const body = {};
      ['username', 'full_name', 'phone', 'role', 'permissions', 'branch_id'].forEach((f) => {
        if (Object.prototype.hasOwnProperty.call(e, f)) body[f] = e[f];
      });
      // Страховка: у пользователя 1-го уровня не должно быть Отчётов/Планов,
      // даже если они остались в правах от прежней роли.
      const effectiveRole = Object.prototype.hasOwnProperty.call(e, 'role') ? e.role : row.role;
      if (Object.prototype.hasOwnProperty.call(body, 'permissions')) {
        body.permissions = prunePermissionsForRole(body.permissions, effectiveRole);
      }
      const resp = await fetch(`${API_BASE}/auth/users/${row.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        let msg = 'Не удалось сохранить изменения.';
        try {
          const j = await resp.json();
          if (j && j.message) msg = j.message;
        } catch { /* ignore */ }
        setError(msg);
        return;
      }
      const updated = await resp.json();
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
      setEdits((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setSavedId(row.id);
    } catch (err) {
      setError('Ошибка подключения к серверу.');
    } finally {
      setSavingId(null);
    }
  }, [edits]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/auth/users/${pendingDelete.id}/`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() },
      });
      if (!resp.ok) {
        let msg = 'Не удалось удалить пользователя.';
        try { const j = await resp.json(); if (j && j.message) msg = j.message; } catch { /* ignore */ }
        setError(msg);
        return;
      }
      setPendingDelete(null);
      // Перезагружаем список (с учётом текущей страницы)
      loadUsers();
    } catch (err) {
      setError('Ошибка подключения к серверу.');
    } finally {
      setDeleting(false);
    }
  };

  const openPasswordModal = useCallback((row) => {
    setPendingPassword({ id: row.id, username: row.username });
    setPwdError(null);
  }, []);

  const openDeleteConfirm = useCallback((row) => {
    setPendingDelete({ id: row.id, username: row.username });
  }, []);

  const submitSearch = useCallback((ev) => {
    ev.preventDefault();
    setPage(1);
    setSearch((searchInputRef.current?.value || '').trim());
  }, []);

  const resetSearch = useCallback(() => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    setSearch('');
    setBranchFilter('');
    setPage(1);
  }, []);

  const closePasswordModal = useCallback(() => {
    if (pwdSaving) return;
    setPendingPassword(null);
  }, [pwdSaving]);

  // Получает уже провалидированный пароль из изолированного ChangePasswordModal.
  const submitPassword = useCallback(async (password) => {
    if (!pendingPassword) return;
    setPwdSaving(true);
    setPwdError(null);
    try {
      const resp = await fetch(`${API_BASE}/auth/users/${pendingPassword.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ password }),
      });
      if (!resp.ok) {
        let msg = 'Не удалось изменить пароль.';
        try { const j = await resp.json(); if (j && j.message) msg = j.message; } catch { /* ignore */ }
        setPwdError(msg);
        return;
      }
      setPwdSavedFor(pendingPassword.id);
      setPendingPassword(null);
      setTimeout(() => setPwdSavedFor(null), 3000);
    } catch (err) {
      setPwdError('Ошибка подключения к серверу.');
    } finally {
      setPwdSaving(false);
    }
  }, [pendingPassword]);

  const openCreate = useCallback(() => {
    setCreateError(null);
    setCreateOpen(true);
  }, []);

  // Получает готовую форму из изолированного CreateUserModal.
  const submitCreate = useCallback(async (form) => {
    if (!form.username.trim()) { setCreateError('Укажите почту (логин).'); return; }
    if (!form.password) { setCreateError('Укажите пароль.'); return; }
    setCreateSaving(true);
    setCreateError(null);
    try {
      const body = {
        username: form.username.trim(),
        password: form.password,
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        role: form.role,
        permissions: form.permissions,
        branch_id: form.branch_id === '' ? null : Number(form.branch_id),
      };
      const resp = await fetch(`${API_BASE}/auth/users/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        let msg = 'Не удалось создать пользователя.';
        try { const j = await resp.json(); if (j && j.message) msg = j.message; } catch { /* ignore */ }
        setCreateError(msg);
        return;
      }
      setCreateOpen(false);
      setPage(1);
      loadUsers();
    } catch (err) {
      setCreateError('Ошибка подключения к серверу.');
    } finally {
      setCreateSaving(false);
    }
  }, [loadUsers]);

  const rangeText = useMemo(() => {
    if (total === 0) return 'Нет пользователей';
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `${start}–${end} из ${total}`;
  }, [page, pageSize, total]);

  const containerStyle = embedded
    ? { flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#fafafa', minHeight: 0 }
    : { width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#fafafa' };

  return (
    <div style={containerStyle}>
      {/* Собственная шапка показывается только в автономном режиме */}
      {!embedded && (
        <div className="top-menu bb-1" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 24px', minHeight: '55px', borderBottom: '1px solid #ddd', backgroundColor: '#fff',
        }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#495057' }}>Управление пользователями</span>
          {onBack && (
            <button type="button" style={btnStyle('secondary')} onClick={onBack}>← Назад</button>
          )}
        </div>
      )}

      <div ref={scrollRef} className="styled-scroll" style={{ flex: 1, overflowY: 'auto', padding: '24px', paddingTop: embedded ? '28px' : '24px' }}>
        {/* Заголовок страницы — в том же стиле, как «Журнал карточек ВНН» */}
        {embedded && (
          <p className="sz-1-3 mt0 b-7" style={{ margin: 0, padding: 0, marginBottom: '64px' }}>
            Список пользователей
          </p>
        )}
        {/* Панель управления: создание — слева, поиск — справа */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '32px' }}>
          <button type="button" style={btnStyle('primary')} onClick={openCreate}>
            + Создать пользователя
          </button>
          <form
            style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}
            onSubmit={submitSearch}
          >
            <select
              aria-label="Филиал"
              style={{ ...searchInputStyle, width: '220px' }}
              value={branchFilter}
              onChange={(ev) => { setPage(1); setBranchFilter(ev.target.value); }}
            >
              <option value="">Все филиалы</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              <option value="none">Без филиала</option>
            </select>
            <input
              ref={searchInputRef}
              style={{ ...searchInputStyle, width: '280px' }}
              placeholder="Поиск по почте, ФИО, телефону, филиалу…"
              defaultValue={search}
            />
            <button type="submit" style={btnStyle('primary')}>Найти</button>
            {(search || branchFilter) && (
              <button type="button" style={btnStyle('secondary')} onClick={resetSearch}>
                Сбросить
              </button>
            )}
          </form>
        </div>

        {error && (
          <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '6px', backgroundColor: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        {/* Таблица */}
        <div style={{ overflowX: 'auto', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1430px' }}>
            <thead>
              <tr>
                <th style={{ ...th, width: '50px' }}>ID</th>
                <th style={{ ...th, width: '130px' }}>Дата создания</th>
                <th style={{ ...th, minWidth: '220px' }}>Почта (логин)</th>
                <th style={{ ...th, minWidth: '180px' }}>ФИО</th>
                <th style={{ ...th, minWidth: '140px' }}>Телефон</th>
                <th style={{ ...th, minWidth: '200px' }}>Уровень</th>
                <th style={{ ...th, minWidth: '170px' }}>Филиал</th>
                <th style={{ ...th, minWidth: '150px', textAlign: 'center' }}>Пароль</th>
                <th style={{ ...th, minWidth: '250px' }}>Права</th>
                <th style={{ ...th, width: '150px' }}>Действие</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td style={{ ...td, textAlign: 'center', padding: '8px' }} colSpan={10}><TreeLoader label="Загрузка пользователей…" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td style={{ ...td, textAlign: 'center', padding: '24px' }} colSpan={10}>Пользователи не найдены</td></tr>
              ) : (
                rows.map((row) => {
                  const isAdmin = row.role === 'LEVEL4';
                  const role = getValue(row, 'role') || 'LEVEL1';
                  const perms = getValue(row, 'permissions') || [];
                  const branchId = getValue(row, 'branch_id');
                  const changed = isRowChanged(row.id);
                  return (
                    <tr key={row.id} style={{ backgroundColor: changed ? '#f3f6fb' : (row.id % 2 === 0 ? '#fcfcfd' : '#fff') }}>
                      <td style={{ ...td, textAlign: 'center', color: '#6c757d', fontWeight: 500 }}>{row.id}</td>
                      <td style={{ ...td, color: '#6c757d', whiteSpace: 'nowrap' }}>{formatDateJoined(row.date_joined)}</td>
                      <td style={tdField}>
                        <input style={{ ...cellFieldStyle, fontWeight: 500 }} defaultValue={getValue(row, 'username') || ''}
                          disabled={isAdmin}
                          onBlur={(ev) => {
                            if (ev.target.value !== (getValue(row, 'username') || '')) {
                              setValue(row.id, 'username', ev.target.value);
                            }
                          }} />
                      </td>
                      <td style={tdField}>
                        <input style={cellFieldStyle} defaultValue={getValue(row, 'full_name') || ''}
                          disabled={isAdmin}
                          onBlur={(ev) => {
                            if (ev.target.value !== (getValue(row, 'full_name') || '')) {
                              setValue(row.id, 'full_name', ev.target.value);
                            }
                          }} />
                      </td>
                      <td style={tdField}>
                        <input style={cellFieldStyle} defaultValue={getValue(row, 'phone') || ''}
                          disabled={isAdmin}
                          onBlur={(ev) => {
                            if (ev.target.value !== (getValue(row, 'phone') || '')) {
                              setValue(row.id, 'phone', ev.target.value);
                            }
                          }} />
                      </td>
                      <td style={tdField}>
                        {isAdmin ? (
                          <span style={{ color: '#888', padding: '8px 12px', display: 'inline-block' }}>Администратор</span>
                        ) : (
                          <select style={cellFieldStyle} value={role}
                            onChange={(ev) => {
                              const newRole = ev.target.value;
                              setValue(row.id, 'role', newRole);
                              // Убираем недоступные для роли права (уровень 1 — без Отчётов/Планов)
                              const pruned = prunePermissionsForRole(perms, newRole);
                              if (pruned.length !== perms.length) {
                                setValue(row.id, 'permissions', pruned);
                              }
                            }}>
                            {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        )}
                      </td>
                      <td style={tdField}>
                        <select style={cellFieldStyle} value={branchId == null ? '' : String(branchId)}
                          disabled={isAdmin}
                          onChange={(ev) => setValue(row.id, 'branch_id', ev.target.value === '' ? null : Number(ev.target.value))}>
                          <option value="">— не задан —</option>
                          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {isAdmin ? (
                          <span style={{ color: '#888', fontSize: '0.8rem' }}>—</span>
                        ) : (
                          <button type="button" style={btnStyle('secondary')}
                            onClick={() => openPasswordModal(row)}>
                            {pwdSavedFor === row.id ? '✓ Изменён' : 'Изменить пароль'}
                          </button>
                        )}
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {PERMISSION_OPTIONS.map((p) => (
                            <PermissionPill
                              key={p.value}
                              label={p.label}
                              active={perms.includes(p.value)}
                              available={isPermissionAllowedForRole(p.value, role)}
                              disabled={isAdmin}
                              onToggle={() => togglePermission(row, p.value)}
                            />
                          ))}
                        </div>
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {isAdmin ? (
                          <span style={{ color: '#888', fontSize: '0.8rem' }}>—</span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'stretch' }}>
                            <button type="button"
                              style={btnStyle('primary', !changed || savingId === row.id)}
                              disabled={!changed || savingId === row.id}
                              onClick={() => saveRow(row)}>
                              {savingId === row.id ? 'Сохранение…' : (savedId === row.id ? '✓ Сохранено' : 'Сохранить')}
                            </button>
                            <button type="button"
                              style={dangerBtnStyle}
                              onClick={() => setPendingDelete({ id: row.id, username: row.username })}>
                              Удалить
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Пагинация */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', color: '#495057' }}>{rangeText}</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '0.85rem', color: '#495057' }}>Показывать по:</span>
              <select
                style={{ ...searchInputStyle, width: 'auto' }}
                value={pageSize}
                onChange={(ev) => { setPageSize(Number(ev.target.value)); setPage(1); }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button type="button" style={btnStyle('secondary', page <= 1)} disabled={page <= 1}
              onClick={() => setPage(1)}>« Первая</button>
            <button type="button" style={btnStyle('secondary', page <= 1)} disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Назад</button>
            <span style={{ fontSize: '0.85rem', color: '#495057', padding: '0 8px' }}>
              Стр. {page} из {numPages}
            </span>
            <button type="button" style={btnStyle('secondary', page >= numPages)} disabled={page >= numPages}
              onClick={() => setPage((p) => Math.min(numPages, p + 1))}>Вперёд ›</button>
            <button type="button" style={btnStyle('secondary', page >= numPages)} disabled={page >= numPages}
              onClick={() => setPage(numPages)}>Последняя »</button>
          </div>
        </div>
      </div>

      {/* Модальное окно смены пароля пользователя */}
      {pendingPassword && (
        <ChangePasswordModal
          user={pendingPassword}
          saving={pwdSaving}
          error={pwdError}
          onClose={closePasswordModal}
          onSubmit={submitPassword}
        />
      )}

      {/* Модальное окно подтверждения удаления пользователя */}
      {pendingDelete && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '8px', padding: '20px',
            maxWidth: '520px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '16px', fontWeight: 600 }}>
              Удаление пользователя
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#6c757d', lineHeight: 1.5, fontSize: '14px' }}>
              Удалить пользователя «{pendingDelete.username}»? Вместе с ним будут безвозвратно
              удалены все его карточки ВНН. Это действие нельзя отменить.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" style={btnStyle('secondary', deleting)} disabled={deleting}
                onClick={() => setPendingDelete(null)}>Отмена</button>
              <button type="button"
                style={{ ...dangerBtnStyle, height: '38px', backgroundColor: '#b04a4a', color: '#fff', border: '1px solid #b04a4a', opacity: deleting ? 0.6 : 1, cursor: deleting ? 'not-allowed' : 'pointer' }}
                disabled={deleting}
                onClick={confirmDelete}>
                {deleting ? 'Удаление…' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно создания нового пользователя (изолированный компонент) */}
      {createOpen && (
        <CreateUserModal
          branches={branches}
          saving={createSaving}
          error={createError}
          onClose={() => setCreateOpen(false)}
          onSubmit={submitCreate}
        />
      )}
    </div>
  );
};

export default UsersAdminPage;
